import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { endpoints } from '@/services/endpoints'
import { createSharedSseConnection, sseEnabled } from '@/services/sse'
import {
  advanceWorkOrder,
  createWorkOrder,
  deleteWorkOrder,
  fetchWorkOrder,
  fetchWorkOrders,
  orderFromWire,
  saveWorkOrder,
} from './api'
import { subscribeToWorkOrderList } from './liveStream'

/**
 * Work Orders reads and writes. LIVE.
 *
 * `enabled` is the caller's permission gate rather than a flag of this hook's
 * own: the list accepts either `work_orders.manage` or
 * `work_orders.update_assigned`, and a hook that decided for the page could not
 * answer that question.
 *
 * ── Every write sweeps four modules, not one ──────────────────────────────
 * A work order is not only on this board. Lines & Assets counts open orders in
 * its five-card stat bar, on every asset row and inside the asset's own Work
 * Orders tab; the public page reads the collection for its summary. Completing
 * an order therefore has to invalidate `['assets']` as well as
 * `['work-orders']`, or the machine keeps claiming work that is finished — which
 * is exactly the kind of quiet disagreement `isOpenOrder` was introduced to
 * stop.
 */
/**
 * The collection.
 *
 * `placeholderData` keeps the board on screen while a filter lands. Six columns
 * of cards blanking together and reappearing is a page that jumps under the
 * reader on every chip.
 */
export const useWorkOrderBoard = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.workOrders(params),
    queryFn: ({ signal }) => fetchWorkOrders(params, signal),
    placeholderData: (previous) => previous,
    enabled,
  })

export const useWorkOrder = (id, { enabled = true } = {}) =>
  useQuery({
    queryKey: queryKeys.workOrder(id),
    queryFn: ({ signal }) => fetchWorkOrder(id, signal),
    enabled: enabled && Boolean(id),
    /* Zero, for the reason `useConnection` and `useSensor` set it: `PUT` is a
       full replace and the form sends back everything it was given, so a stale
       prefill is how a field somebody else changed gets silently restored. */
    staleTime: 0,
    /* `useWorkOrderStream` writes into this key and lives exactly as long as
       the dialog that reads it; nothing should outlive that. */
    gcTime: 0,
  })

/**
 * Keep the board live — `GET /work-orders/stream`. Opt-in: mounting this is
 * what makes a board live. Each event refetches every mounted board page, and
 * Lines & Assets' open-order counts with it (see the note at the top).
 *
 * @returns {{status: 'idle'|'connecting'|'live'|'retrying'|'failed'}}
 */
export function useWorkOrderListStream({ enabled = true } = {}) {
  const client = useQueryClient()
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    if (!enabled || !sseEnabled()) return undefined
    return subscribeToWorkOrderList(() => {
      client.invalidateQueries({ queryKey: ['work-orders', 'list'] })
      client.invalidateQueries({ queryKey: ['assets'] })
    }, setStatus)
  }, [enabled, client])

  return { status }
}

/**
 * Keep one order live — `GET /work-orders/{id}/stream`.
 *
 * The event carries the full `WorkOrderOut`, so it is applied rather than
 * refetched, unless an older copy would overwrite a newer one. An event with no
 * row (the server pops its payload, so a second listener can be handed nothing)
 * falls back to a refetch. Not shared and not lingered: one open order, one
 * socket, closed with it.
 */
export function useWorkOrderStream(id, { enabled = true } = {}) {
  const client = useQueryClient()
  const [status, setStatus] = useState('idle')

  useEffect(() => {
    if (!id || !enabled || !sseEnabled()) return undefined

    const connection = createSharedSseConnection({
      path: endpoints.workOrders.detailStream(id),
      lingerMs: 0,
      onEvent: (event) => {
        if (!event?.id) {
          client.invalidateQueries({ queryKey: queryKeys.workOrder(id) })
        } else {
          const incoming = orderFromWire(event)
          client.setQueryData(queryKeys.workOrder(id), (previous) =>
            previous?.updatedAt && incoming.updatedAt && previous.updatedAt > incoming.updatedAt
              ? previous
              : incoming,
          )
        }
        /* The board card behind it moved too — its column at least. */
        client.invalidateQueries({ queryKey: ['work-orders', 'list'] })
      },
      onOpen: ({ reconnected }) => {
        if (reconnected) client.invalidateQueries({ queryKey: queryKeys.workOrder(id) })
      },
      onStatus: setStatus,
    })

    return connection.subscribe()
  }, [id, enabled, client])

  return { status }
}

/** Everything a work-order write has to invalidate. */
function useSweep() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['work-orders'] })
    // Lines & Assets counts open orders in three places. See the note above.
    queryClient.invalidateQueries({ queryKey: ['assets'] })
  }
}

export const useCreateWorkOrder = () => {
  const sweep = useSweep()
  return useMutation({ mutationFn: createWorkOrder, onSuccess: sweep })
}

/**
 * The form's save. A full replace — see `saveWorkOrder` for why there is no
 * narrower call, and why the prefill it is built from must not be stale.
 */
export const useSaveWorkOrder = () => {
  const sweep = useSweep()
  return useMutation({ mutationFn: saveWorkOrder, onSuccess: sweep })
}

export const useAdvanceWorkOrder = () => {
  const sweep = useSweep()
  return useMutation({ mutationFn: advanceWorkOrder, onSuccess: sweep })
}

export const useDeleteWorkOrder = () => {
  const sweep = useSweep()
  return useMutation({ mutationFn: deleteWorkOrder, onSuccess: sweep })
}
